import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import {
  AdvancedBinNamingTemplateSchema,
  BinNamingSettingsSchema,
} from "../bin-naming";
import {
  BinPrioritySchema,
  PersistentStorageLocationTypeSchema,
} from "./storage-location";

export const CoordinateSchema = z.object({ x: z.number(), y: z.number() });
export type Coordinate = z.infer<typeof CoordinateSchema>;
export interface ICoordinate extends z.ZodType<Coordinate> {}

export enum CellType {
  RACK = "RACK",
  WALKWAY = "WALKWAY",
}

export enum RackType {
  STANDARD = "STANDARD",
  OVERHEAD = "OVERHEAD",
}

export enum Direction {
  UP = "UP",
  DOWN = "DOWN",
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

const WarehouseCellBase = z.object({
  x: z.number(),
  y: z.number(),
  type: createEnum(CellType, { enumName: "CellType" }),
});

/** Defines a range of bins with an associated storage location type */
export const StorageLocationTypeRangeSchema_ = z.object({
  /** Starting index of the range (0-based, inclusive) */
  start: z.number().int().min(0),
  /** Ending index of the range (0-based, inclusive) */
  end: z.number().int().min(0),
  /** Storage location type for bins in this range */
  type: PersistentStorageLocationTypeSchema,
});
export const StorageLocationTypeRangeSchema: z.ZodType<StorageLocationTypeRange> =
  StorageLocationTypeRangeSchema_;
export type StorageLocationTypeRange = z.infer<
  typeof StorageLocationTypeRangeSchema_
>;

/** A custom bin with a name and storage location type */
export const CustomBinSchema_ = z.object({
  name: z.string(),
  type: PersistentStorageLocationTypeSchema,
});
export const CustomBinSchema: z.ZodType<CustomBin> = CustomBinSchema_;
export type CustomBin = z.infer<typeof CustomBinSchema_>;

export const RackCellSchema = WarehouseCellBase.extend({
  type: z.literal(CellType.RACK),
  bins: z.array(z.string()),
  accessibleDirections: z.array(
    createEnum(Direction, { enumName: "Direction" }),
  ),
  name: z.string().nullish(),
  shelfCount: z.number().nullish(),
  rowName: z.string().nullish(),
  namingSchema: BinNamingSettingsSchema.nullish(),
  advancedNamingTemplate: AdvancedBinNamingTemplateSchema.nullish(),
  rackType: createEnum(RackType, { enumName: "RackType" }).nullish(),
  /** @deprecated Use typedCustomBins instead. One-off bin names without type info */
  customBins: z.array(z.string()).nullish(),
  /** One-off bin names with storage location type */
  typedCustomBins: z.array(CustomBinSchema).nullish(),
  /** Storage location type ranges - defines which bins have which type */
  storageLocationTypeRanges: z.array(StorageLocationTypeRangeSchema).nullish(),
  /** If set, all bins in this rack are this type (overrides storageLocationTypeRanges) */
  rackBinType: PersistentStorageLocationTypeSchema.nullish(),
  /** If set, all bins in this rack have this pick priority */
  rackPriority: BinPrioritySchema.nullish(),
});
export type RackCell = z.infer<typeof RackCellSchema>;
export interface IRackCell extends z.ZodType<RackCell> {}

export const WalkwayCellSchema = WarehouseCellBase.extend({
  type: z.literal(CellType.WALKWAY),
});
export type WalkwayCell = z.infer<typeof WalkwayCellSchema>;

export const WarehouseCellSchema = z.discriminatedUnion("type", [
  RackCellSchema,
  WalkwayCellSchema,
]);

export type WarehouseCell = z.infer<typeof WarehouseCellSchema>;

export const WallSchema = z.object({
  from: CoordinateSchema,
  to: CoordinateSchema,
});
export type Wall = z.infer<typeof WallSchema>;
